import { compose, createStore as createStore$1, applyMiddleware } from 'redux';
import memoizerific from 'memoizerific';
import produce, { setAutoFreeze } from 'immer';
import reduxThunk from 'redux-thunk';
import React, { createContext, useContext, useState, useRef, useEffect } from 'react';
import shallowEqual from 'shallowequal';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

var isStateObject = function isStateObject(x) {
  return x !== null && typeof x === 'object' && !Array.isArray(x);
};
/**
 * immer is an implementation detail, so we are not going to use its auto freeze
 * behaviour, which throws errors if trying to mutate state. It's also risky
 * for production builds as has a perf overhead.
 *
 * @see https://github.com/mweststrate/immer#auto-freezing
 */


setAutoFreeze(false);
var maxSelectFnMemoize = 100;
var actionNameSymbol = '__actionName__';
var effectSymbol = '__effect__';
var thunkSymbol = '__thunk__';
var listenersSymbol = '__listeners__';
var listenSymbol = '__listen__';
var metaSymbol = '__meta__';
var selectSymbol = '__select__';
var selectImpSymbol = '__selectImp__';
var selectDependenciesSymbol = '__selectDependencies__';
var selectStateSymbol = '__selectState__';
var reducerSymbol = '__reducer__';

var get = function get(path, target) {
  return path.reduce(function (acc, cur) {
    return isStateObject(acc) ? acc[cur] : undefined;
  }, target);
};

var set = function set(path, target, value) {
  path.reduce(function (acc, cur, idx) {
    if (idx + 1 === path.length) {
      acc[cur] = value;
    } else {
      acc[cur] = acc[cur] || {};
    }

    return acc[cur];
  }, target);
};

var tick = function tick() {
  return new Promise(function (resolve) {
    return setTimeout(resolve);
  });
};

var startsWith = function startsWith(target, search) {
  return target.substr(0, search.length) === search;
};

var notifiedAboutEffectDeprecation = false;

var actionName = function actionName(action) {
  return action[actionNameSymbol];
};

var thunkStartName = function thunkStartName(action) {
  return action[actionNameSymbol] + "(started)";
};

var thunkCompleteName = function thunkCompleteName(action) {
  return action[actionNameSymbol] + "(completed)";
};

var effect = function effect(fn) {
  if (!notifiedAboutEffectDeprecation) {
    notifiedAboutEffectDeprecation = true; // eslint-disable-next-line no-console

    console.warn('Easy Peasy: warning we have deprecated the "effect" helper. It will be removed in the next major release. We recommend that you migrate to the "thunk" helper, which satisfies the same responsibilities.');
  }

  fn[effectSymbol] = true;
  return fn;
};

var thunk = function thunk(fn) {
  fn[thunkSymbol] = true;
  return fn;
};

var select = function select(fn, dependencies) {
  fn[selectSymbol] = true;
  fn[selectDependenciesSymbol] = dependencies;
  fn[selectStateSymbol] = {};
  return fn;
};

var reducer = function reducer(fn) {
  fn[reducerSymbol] = true;
  return fn;
};

var notifiedAboutListenersDeprecation = false;

var listeners = function listeners(fn) {
  if (!notifiedAboutListenersDeprecation) {
    notifiedAboutListenersDeprecation = true; // eslint-disable-next-line no-console

    console.warn('Easy Peasy: warning we have deprecated the "listeners" helper. It will be removed in the next major release. We recommend that you migrate to the "listen" helper, which satisfies the same responsibilities.');
  }

  fn[listenersSymbol] = true;
  return fn;
};

var listen = function listen(fn) {
  fn[listenSymbol] = true;
  return fn;
};

var createStore = function createStore(model, options) {
  if (options === void 0) {
    options = {};
  }

  var _options = options,
      compose$1 = _options.compose,
      _options$devTools = _options.devTools,
      devTools = _options$devTools === void 0 ? true : _options$devTools,
      _options$disableInter = _options.disableInternalSelectFnMemoize,
      disableInternalSelectFnMemoize = _options$disableInter === void 0 ? false : _options$disableInter,
      _options$initialState = _options.initialState,
      initialState = _options$initialState === void 0 ? {} : _options$initialState,
      injections = _options.injections,
      _options$mockActions = _options.mockActions,
      mockActions = _options$mockActions === void 0 ? false : _options$mockActions,
      _options$middleware = _options.middleware,
      middleware = _options$middleware === void 0 ? [] : _options$middleware,
      _options$reducerEnhan = _options.reducerEnhancer,
      reducerEnhancer = _options$reducerEnhan === void 0 ? function (rootReducer) {
    return rootReducer;
  } : _options$reducerEnhan;

  var wrapFnWithMemoize = function wrapFnWithMemoize(x) {
    return !disableInternalSelectFnMemoize && typeof x === 'function' ? memoizerific(maxSelectFnMemoize)(x) : x;
  };

  var definition = _extends({}, model, {
    logFullState: thunk(function (actions, payload, _ref) {
      var getState = _ref.getState; // eslint-disable-next-line no-console

      console.log(JSON.stringify(getState(), null, 2));
    })
  });

  var references = {};
  var defaultState = {};
  var actionThunks = {};
  var actionCreators = {};
  var actionCreatorDict = {};
  var actionReducers = {};
  var customReducers = [];
  var selectorReducers = [];
  var listenerDefinitions = [];
  var listenerDict = {};
  var listenDefinitions = [];
  var listenDict = {};
  var listenStringDict = {};

  var dispatchListenersForAction = function dispatchListenersForAction(name, payload) {
    var listenersForAction = listenerDict[name];
    var listensForAction = [].concat(listenDict[name] || [], listenStringDict[name] || []);
    return Promise.all([listenersForAction && listenersForAction.length > 0 ? Promise.all(listenersForAction.map(function (listenerForAction) {
      return listenerForAction(references.dispatch, payload, references.getState, injections);
    })) : Promise.resolve(), listensForAction && listensForAction.length > 0 ? Promise.all(listensForAction.map(function (listenForAction) {
      return listenForAction(get(listenForAction[metaSymbol].parent, actionCreators), payload, {
        dispatch: references.dispatch,
        getState: references.getState,
        injections: injections,
        meta: listenForAction[metaSymbol]
      });
    })) : Promise.resolve()]);
  };

  var extract = function extract(current, parentPath) {
    return Object.keys(current).forEach(function (key) {
      var value = current[key];
      var path = [].concat(parentPath, [key]);
      var meta = {
        parent: parentPath,
        path: path
      };

      if (typeof value === 'function') {
        if (value[selectSymbol]) {
          // skip
          value[selectStateSymbol] = {
            parentPath: parentPath,
            key: key,
            executed: false
          };
          selectorReducers.push(value);
        } else if (value[thunkSymbol]) {
          var name = "@thunk." + path.join('.');
          value[actionNameSymbol] = name; // Thunk Action

          var action = function action(payload) {
            return value(get(parentPath, actionCreators), payload, {
              dispatch: references.dispatch,
              getState: references.getState,
              injections: injections,
              meta: meta
            });
          };

          set(path, actionThunks, action); // Thunk Action Creator

          var actionCreator = function actionCreator(payload) {
            return tick().then(function () {
              return references.dispatch({
                type: name + "(started)",
                payload: payload
              });
            }).then(function () {
              return references.dispatch(function () {
                return action(payload);
              });
            }).then(function (result) {
              references.dispatch({
                type: name + "(completed)",
                payload: payload
              });
              dispatchListenersForAction(actionCreator[actionNameSymbol], payload);
              return result;
            });
          };

          actionCreator[actionNameSymbol] = name;
          actionCreatorDict[name] = actionCreator;
          set(path, actionCreators, actionCreator);
        } else if (value[effectSymbol]) {
          var _name = "@effect." + path.join('.');

          value[actionNameSymbol] = _name; // Effect Action

          var _action = function _action(payload) {
            if (devTools) {
              references.dispatch({
                type: _name,
                payload: payload
              });
            }

            return value(references.dispatch, payload, references.getState, injections, {
              parent: parentPath,
              path: path
            });
          }; // Effect Action Creator


          var _actionCreator = function _actionCreator(payload) {
            return tick().then(function () {
              return references.dispatch(function () {
                return _action(payload);
              });
            }).then(function (result) {
              dispatchListenersForAction(_actionCreator[actionNameSymbol], payload);
              return result;
            });
          };

          _actionCreator[actionNameSymbol] = _name;
          actionCreatorDict[_name] = _actionCreator;
          set(path, actionCreators, _actionCreator);
        } else if (value[reducerSymbol]) {
          customReducers.push({
            path: path,
            reducer: value
          });
        } else if (value[listenersSymbol]) {
          listenerDefinitions.push(value);
        } else if (value[listenSymbol]) {
          listenDefinitions.push(value);
          value[metaSymbol] = meta;
        } else {
          var _name2 = "@action." + path.join('.');

          value[actionNameSymbol] = _name2; // Reducer Action

          var _action2 = function _action2(state, payload) {
            return produce(state, function (draft) {
              return value(draft, payload, {
                dispatch: references.dispatch,
                dispatchLocal: get(path, references.dispatch),
                getState: references.getState
              });
            });
          };

          _action2.actionName = _name2;
          set(path, actionReducers, _action2); // Reducer Action Creator

          var _actionCreator2 = function _actionCreator2(payload) {
            var result = references.dispatch({
              type: _action2.actionName,
              payload: payload
            });
            dispatchListenersForAction(_actionCreator2[actionNameSymbol], payload);
            return result;
          };

          _actionCreator2[actionNameSymbol] = _name2;
          actionCreatorDict[_name2] = _actionCreator2;
          set(path, actionCreators, _actionCreator2);
        }
      } else if (isStateObject(value) && Object.keys(value).length > 0) {
        set(path, defaultState, {});
        extract(value, path);
      } else {
        // State
        var initialParentRef = get(parentPath, initialState);

        if (initialParentRef && key in initialParentRef) {
          set(path, defaultState, initialParentRef[key]);
        } else {
          set(path, defaultState, value);
        }
      }
    });
  };

  extract(definition, []);
  selectorReducers.forEach(function (selector) {
    selector[selectImpSymbol] = memoizerific(1)(function (state) {
      return wrapFnWithMemoize(selector(state));
    });
  });
  listenDefinitions.forEach(function (def) {
    var on = function on(action, thunkHandler) {
      if (typeof thunkHandler !== 'function') {
        return;
      }

      thunkHandler[metaSymbol] = def[metaSymbol];

      if (typeof action === 'function' && action[actionNameSymbol] && actionCreatorDict[action[actionNameSymbol]]) {
        listenDict[action[actionNameSymbol]] = listenDict[action[actionNameSymbol]] || [];
        listenDict[action[actionNameSymbol]].push(thunkHandler);
      } else if (typeof action === 'string') {
        listenStringDict[action] = listenStringDict[action] || [];
        listenStringDict[action].push(thunkHandler);
      }
    };

    def(on);
  });
  listenerDefinitions.forEach(function (def) {
    var on = function on(actionCreator, handler) {
      if (typeof actionCreator === 'function' && actionCreator[actionNameSymbol] && actionCreatorDict[actionCreator[actionNameSymbol]]) {
        listenerDict[actionCreator[actionNameSymbol]] = listenerDict[actionCreator[actionNameSymbol]] || [];
        listenerDict[actionCreator[actionNameSymbol]].push(handler);
      }
    };

    def(actionCreators, on);
  });

  var createReducers = function createReducers() {
    var createActionsReducer = function createActionsReducer(current, path) {
      var actionReducersAtPath = Object.keys(current).reduce(function (acc, key) {
        var value = current[key];

        if (typeof value === 'function' && !value[effectSymbol]) {
          return [].concat(acc, [value]);
        }

        return acc;
      }, []);
      var stateAtPath = Object.keys(current).reduce(function (acc, key) {
        return isStateObject(current[key]) ? [].concat(acc, [key]) : acc;
      }, []);
      var nestedReducers = stateAtPath.map(function (key) {
        return [key, createActionsReducer(current[key], [].concat(path, [key]))];
      });
      return function (state, action) {
        if (state === void 0) {
          state = get(path, defaultState);
        } // short circuit effects as they are noop in reducers


        if (startsWith(action.type, '@effect.')) {
          return state;
        } // short circuit actions if they aren't a match on current path


        if (path.length > 0 && !startsWith(action.type, "@action." + path.join('.'))) {
          return state;
        }

        var actionReducer = actionReducersAtPath.find(function (x) {
          return x.actionName === action.type;
        });

        if (actionReducer) {
          return actionReducer(state, action.payload);
        }

        for (var i = 0; i < nestedReducers.length; i += 1) {
          var _nestedReducers$i = nestedReducers[i],
              key = _nestedReducers$i[0],
              red = _nestedReducers$i[1];
          var newState = red(state[key], action);

          if (state[key] !== newState) {
            var _extends2;

            return _extends({}, state, (_extends2 = {}, _extends2[key] = newState, _extends2));
          }
        }

        return state;
      };
    };

    var reducerForActions = createActionsReducer(actionReducers, []);
    var reducerWithCustom = customReducers.length > 0 ? function (state, action) {
      var stateAfterActions = reducerForActions(state, action);
      return produce(stateAfterActions, function (draft) {
        customReducers.forEach(function (_ref2) {
          var p = _ref2.path,
              red = _ref2.reducer;
          var current = get(p, draft);
          set(p, draft, red(current, action));
        });
      });
    } : reducerForActions;

    var runSelectorReducer = function runSelectorReducer(state, selector) {
      var _selector$selectState = selector[selectStateSymbol],
          parentPath = _selector$selectState.parentPath,
          key = _selector$selectState.key,
          executed = _selector$selectState.executed;

      if (executed) {
        return state;
      }

      var dependencies = selector[selectDependenciesSymbol];
      var stateAfterDependencies = dependencies ? dependencies.reduce(runSelectorReducer, state) : state;
      var newState = state;

      if (parentPath.length > 0) {
        var target = get(parentPath, stateAfterDependencies);

        if (target) {
          var newValue = selector[selectImpSymbol](target);
          newState = produce(state, function (draft) {
            var updateTarget = get(parentPath, draft);
            updateTarget[key] = newValue;
          });
        }
      } else {
        var _newValue = selector[selectImpSymbol](stateAfterDependencies);

        newState = produce(state, function (draft) {
          draft[key] = _newValue;
        });
      }

      selector[selectStateSymbol].executed = true;
      return newState;
    };

    var runSelectors = function runSelectors(state) {
      return selectorReducers.reduce(runSelectorReducer, state);
    };

    var isInitial = true;
    return selectorReducers.length > 0 ? function (state, action) {
      var stateAfterActions = reducerWithCustom(state, action);

      if (state !== stateAfterActions || isInitial) {
        var stateAfterSelectors = runSelectors(stateAfterActions);
        isInitial = false;
        selectorReducers.forEach(function (selector) {
          selector[selectStateSymbol].executed = false;
        });
        return stateAfterSelectors;
      }

      return stateAfterActions;
    } : reducerWithCustom;
  };

  var reducers = createReducers();
  var composeEnhancers = compose$1 || (devTools && typeof window !== 'undefined' && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ : compose);

  var dispatchActionStringListeners = function dispatchActionStringListeners() {
    return function (next) {
      return function (action) {
        if (listenStringDict[action.type]) {
          dispatchListenersForAction(action.type, action.payload);
        }

        return next(action);
      };
    };
  };

  var mockedActions = [];

  var mockActionsMiddlware = function mockActionsMiddlware() {
    return function (next) {
      return function (action) {
        if (mockActions) {
          mockedActions.push(action);
          return undefined;
        }

        return next(action);
      };
    };
  };

  var store = createStore$1(reducerEnhancer(reducers), defaultState, composeEnhancers(applyMiddleware.apply(void 0, [reduxThunk, dispatchActionStringListeners, mockActionsMiddlware].concat(middleware))));

  store.getMockedActions = function () {
    return [].concat(mockedActions);
  };

  store.clearMockedActions = function () {
    mockedActions = [];
  }; // attach the action creators to dispatch


  Object.keys(actionCreators).forEach(function (key) {
    store.dispatch[key] = actionCreators[key];
  });
  references.dispatch = store.dispatch;
  references.getState = store.getState;
  references.getState.getState = store.getState;
  return store;
};

var StoreContext = createContext();

function useStore(mapState, dependencies) {
  if (dependencies === void 0) {
    dependencies = [];
  }

  var store = useContext(StoreContext);

  var _useState = useState(mapState(store.getState())),
      state = _useState[0],
      setState = _useState[1]; // As our effect only fires on mount and unmount it won't have the state
  // changes visible to it, therefore we use a mutable ref to track this.


  var stateRef = useRef(state); // Helps avoid firing of events when unsubscribed, i.e. unmounted

  var isActive = useRef(true);
  useEffect(function () {
    var calculateState = function calculateState() {
      var newState = mapState(store.getState());
      isActive.current = true;

      if (newState === stateRef.current || isStateObject(newState) && isStateObject(stateRef.current) && shallowEqual(newState, stateRef.current)) {
        // Do nothing
        return;
      }

      stateRef.current = newState;

      if (isActive.current) {
        setState(stateRef.current);
      }
    };

    calculateState();
    var unsubscribe = store.subscribe(calculateState);
    return function () {
      unsubscribe();
      isActive.current = false;
    };
  }, dependencies);
  return state;
}

var warnedAboutUseActionDeprecation = false;

function useAction(mapActions) {
  if (!warnedAboutUseActionDeprecation) {
    warnedAboutUseActionDeprecation = true; // eslint-disable-next-line no-console

    console.warn('Easy Peasy: the "useAction" hook has been deprecated and will be removed in the next major release. We recommend using the "useActions" hook instead.');
  }

  var store = useContext(StoreContext);
  return mapActions(store.dispatch);
}

function useActions(mapActions) {
  var store = useContext(StoreContext);
  return mapActions(store.dispatch);
}

function useDispatch() {
  var store = useContext(StoreContext);
  return store.dispatch;
}

function createTypedHooks() {
  return {
    useActions: useActions,
    useAction: useAction,
    useDispatch: useDispatch,
    useStore: useStore
  };
}

var StoreProvider = function StoreProvider(_ref) {
  var children = _ref.children,
      store = _ref.store;
  return React.createElement(StoreContext.Provider, {
    value: store
  }, children);
};

export { actionName, createStore, createTypedHooks, effect, listeners, listen, reducer, select, StoreProvider, thunk, thunkStartName, thunkCompleteName, useAction, useActions, useDispatch, useStore };